/**
 * 投诉提交的**唯一一份**规则：什么算合法、附件收不收、请求体怎么拼。
 *
 * ★ 为什么单独抽一个文件（2026-08-26 PC 端接入时立）：
 *   手机端与 PC 端是**两个入口、同一件事**。只要「什么算合法」有两份实现，
 *   迟早会分叉——本项目已经在契约上吃过一次「真本 ＋ 手工镜像、五处分叉」的亏
 *   （见 `src/api/contract.ts` 文件头）。校验规则不许重演。
 *   规则改这里一处，两个入口同时变；谁都不许在页面里另写一套 if。
 *
 * ★ 前端这一道只为让人少跑一趟，**真正的闸门在后端**：
 *   绕过页面直接打 `/api/citizen/complaints`，证据缺失、超量、超大照样被 400 顶回来。
 *   前端拦不住 ≠ 后端放行。
 *
 * ★★ 四个上限的出处（2026-09-04 批 ⑭ 起**全部来自同一份真本**）：
 *   `@cdfjx/shared` 的 `attachment-limits.ts` —— 件数 20、单件 40MB、单案累计 200MB、
 *   请求体 64mb 四行写在一起，后端三个入口与两个前端引的都是它。
 *   改前这几个数在本文件、后端两处、两份契约、后台组件、main.ts 里**各写了一遍**，
 *   放开限额时改漏一处的症状是「界面允许、后端 400」，或反过来「后端收得下、界面不让选」。
 *
 * ★ 「一次提交总量」这一道仍然必须由前端拦：
 *   20 件 × 40MB = 800MB 原文 ≈ 1067MB base64，**根本到不了服务端那道件数校验**，
 *   会先撞 body-parser 的 413，报的是一句英文 `request entity too large`，
 *   市民只会看到「提交失败」而不知道为什么。PC 上一次拖十几件的概率远高于手机。
 *   预算＝请求体闸 − 4MB 余量，值同样来自真本（`TOTAL_B64_BUDGET_BYTES`）。
 *
 * ★★ **单案累计 200MB 这一道前端挡不住**（批 ⑭ 新增，如实记在这里）：
 *   它要读这件案子已存的四份索引，那是服务端才有的信息。所以本文件不假装拦它 ——
 *   超了由后端 400，页面把后端给的中文原文**原样弹出来**（那句话里带着
 *   「这一件多大、上限多少、还剩多少」）。**绝不许在前端编一句「上传失败」把它盖掉。**
 */

import type { CitizenAttachmentInput, CitizenSubmitBody } from '../api/contract';
/* ★ 2026-09-04 批 ⑭：附件限额与话术的**唯一真本**。本文件一个数字都不许写。 */
import {
  ATTACHMENT_ACCEPT_ATTR,
  ATTACHMENT_LIMITS,
  TOTAL_B64_BUDGET_BYTES,
  TOTAL_RAW_BUDGET_BYTES,
  attachmentKind,
  attachmentLimitHint,
  checkAttachmentBytes,
  checkAttachmentType,
  fmtBytes,
  msgBatchOverBudget,
  msgTooManyAttachments,
} from '@cdfjx/shared';
/* ★ 五项要素的取值白名单与字数上限一律取自**契约真本**（`prefill-contract.ts`），
   这里一个都不抄：抄一份就多一处「界面允许、后端 400」的分叉点。 */
import { PREFILL_CASE_DOMAINS, PREFILL_LIMITS } from '@cdfjx/shared/dist/prefill-contract';
import type { PrefillResult, PrefillStatus } from '@cdfjx/shared/dist/prefill-contract';

/** 身份取值照契约注释：买方／卖方／租客／房东／其他 */
export const COMPLAINT_ROLES = ['买方', '卖方', '租客', '房东', '其他'] as const;
export type ComplaintRole = (typeof COMPLAINT_ROLES)[number];

/**
 * 「其他」这一枚的字面值。两端的展开判据都取它，**不许各写一个字符串字面量** ——
 * 那就是两份实现，改一处漏一处会变成「PC 上点其他不展开」。
 */
export const ROLE_OTHER = '其他';

/** 「其他」后面那个补充说明的字数上限（2026-09-03 协会令定：一行装得下、够写一个身份） */
export const MAX_ROLE_OTHER_LEN = 10;

/**
 * 身份字段的**最终取值**（进提交体、也进确认页那一行）。
 *
 * ★★ 2026-09-03 协会令：身份选「其他」后多一个补充输入框，所填的字并进**原有的身份字段**，
 *   约定的形状只有一种：**`其他：<所填>`**（全角冒号）。
 *   · **契约与后端字段一个都不新增** —— 后端 `citizen.service.ts` 对 `投诉人身份`
 *     是「trim 后原样存」（乐享 text 列），本来就吃得下这个串。
 *   · 选了「其他」但**没写字** ＝ 按只选了「其他」提交。身份本来就是选填，
 *     **不新增任何拦截**（`checkComplaint` 一个字没加）。
 *   · 没选「其他」时 `roleOther` 必须已经是空的（两端在换选/取消时当场清空），
 *     这里对 `role !== '其他'` 直接原样返回 —— 万一还有残值也进不了提交体，双保险。
 *
 * ★ 两端共用这一个函数，为的是**提交体与确认页看到的字一定是同一个** ——
 *   各自拼一次就会出现「确认页显示『其他』、落库却是『其他：房东的亲属』」。
 */
export function composeRole(role: string, roleOther: string): string {
  if (role !== ROLE_OTHER) return role;
  const extra = roleOther.trim().slice(0, MAX_ROLE_OTHER_LEN);
  return extra ? `${ROLE_OTHER}：${extra}` : ROLE_OTHER;
}

/**
 * `composeRole` 的**逆向**：把已经落库的一个身份值拆回「选了哪一枚 ＋ 补充框里该显示什么」。
 *
 * ★★ 2026-09-04 F2（主公令做）：补录页要能把 `其他：房东的亲属` 还原成
 *   「『其他』高亮 ＋ 补充框带出『房东的亲属』」。没有这一半，市民进补录页会看到
 *   一枚 chip 都不亮（像没填过），随手点一下就把补充说明**静默覆盖**掉。
 *
 * ★ 与 `composeRole` **必须成对**。往返恒等式：
 *     **对 `composeRole` 能产出的每一个值 v，`composeRole(...parseRole(v)) === v` 恒成立。**
 *   （这条已写成断言实跑，覆盖 5 枚身份 × 空/普通/满 10 字 的全部组合。）
 *
 * ★ 但对**非规范输入**（`composeRole` 产不出来的串）往返**不是**恒等，而是**归一**，
 *   这是有意的，不是缺陷：
 *     · `'其他：'`（有冒号没内容）→ 归一成 `'其他'`
 *       —— 契约就是「填了其他不写字＝只选了其他」，若为了往返相等而让 compose 吐出
 *          `'其他：'`，等于凭空造一个空补充说明。
 *     · `'其他：<11 字以上>'` → 截到 10 字
 *       —— 上限只有一处（`MAX_ROLE_OTHER_LEN`），不许因为是旧值就放行。
 *     · 前后空白 → trim 掉。
 *   归一之后**再跑一次往返就稳定**（幂等），这一条也写进了断言。
 *
 * ★★ 正因为存在归一，**补录页绝不许在加载时把 draft 里的值改写成归一值** ——
 *   那会让一个「市民一个字没动」的字段被 `diff()` 判成改过、白白发一次修改。
 *   `Amend.tsx` 的做法是：draft 里原样存服务端给的串，只在**渲染**时 parse 出来用，
 *   市民真去点了才 compose 写回。见那边的注释。
 */
export function parseRole(v: string): { role: string; roleOther: string } {
  const t = String(v ?? '').trim();
  if (!t) return { role: '', roleOther: '' };
  if (t === ROLE_OTHER) return { role: ROLE_OTHER, roleOther: '' };
  const prefix = `${ROLE_OTHER}：`;
  if (t.startsWith(prefix)) {
    return { role: ROLE_OTHER, roleOther: t.slice(prefix.length).trim().slice(0, MAX_ROLE_OTHER_LEN) };
  }
  // 其余（买方／卖方／租客／房东，以及任何老数据里的怪值）原样交回：
  // 是不是合法身份由 `validateAmendValue` 判，parse 这一层不替它下结论。
  return { role: t, roleOther: '' };
}

/**
 * `<input type="file" accept>` 的取值。**四个取件入口共用这一个** ——
 * 投诉页、PC 投诉页、补录页、答辩页各写一遍的话，迟早出现「这一页能选录音、
 * 那一页选不了」，而且没有任何测试会红。值来自真本，**不许退化成「星杠星」**。
 */
export const evidenceAcceptAttr = ATTACHMENT_ACCEPT_ATTR;

/** 界面上「最多几件、单件多大、能传什么」那一句，四个入口共用。 */
export const attachmentLimitHintText = attachmentLimitHint();

/** 一次最多几件。**只是转出口**，值在真本里（当前 20）。 */
export const MAX_FILES = ATTACHMENT_LIMITS.maxAttachments;

/**
 * 单件上限。**只是转出口**，值在真本里（当前 40MB）。
 *
 * ★ 口径修正（2026-08-26，仍然有效）：手机端原先拿**base64 串长度**去比这个数，
 *   而后端比的是 `Buffer.from(b64,'base64').length` ＝**解码后的字节数**。
 *   base64 比原文胖 4/3，于是前端实际上把上限收窄了四分之一，
 *   并且弹出来的话是「「x.jpg」8.0MB，超过单张 10.0MB」—— 数字自相矛盾，市民只会以为系统坏了。
 *   现在一律拿 `File.size`（＝解码后字节数）比，与后端严丝合缝。
 */
export const MAX_FILE_BYTES = ATTACHMENT_LIMITS.maxAttachmentBytes;

/** 服务端 body-parser 的硬上限（真本给，`main.ts` 用的也是它），仅用于算下面那个预算 */
export const SERVER_BODY_LIMIT = ATTACHMENT_LIMITS.serverBodyLimitBytes;

/**
 * 一次提交所有附件的 base64 总量预算。
 *
 * 留 4MB 余量给 JSON 本身（字段名、原文、引号逗号）与 HTTP 头——
 * 卡到 64MB 整会在边界上偶发 413，那种"有时候能提交有时候不能"最难查。
 *
 * ★ 2026-09-04 批 ⑭：60MB 的 base64 ≈ 45MB 原文。也就是说「一次能传一件 40MB 的，
 *   或者若干件小的」。这不是限额口径变了，是请求体闸决定的物理上限，
 *   话必须对市民说清楚（`msgBatchOverBudget` 里那句「分两次提交」）。
 */
export const TOTAL_B64_BUDGET = TOTAL_B64_BUDGET_BYTES;

/** 投诉人姓名长度上限 */
export const MAX_NAME_LEN = 20;

/** 一件已选中的证据。`preview` / `bytes` / `kind` 只服务界面，不进请求体。 */
export interface PickedEvidence {
  name: string;
  contentType: string;
  /** base64，不带 `data:` 前缀 —— 契约 `CitizenAttachmentInput` 要的就是这个 */
  contentBase64: string;
  /**
   * dataURL，仅用于本地缩略图。
   *
   * ★★ 2026-09-04 批 ⑭：**只有图片才有值，音频/视频/文档一律空串**。
   *   放开录音与视频之后如果照旧把 dataURL 塞进 `<img src>`，用户会看到一格
   *   碎图标 —— 那看起来就像「这件传坏了」。空串是给界面的明确信号：
   *   请按 `kind` 画一个类别标记，不要画缩略图。
   */
  preview: string;
  /** 原始文件字节数，用于给人看「这件多大」 */
  bytes: number;
  /** 类别，界面据此决定画缩略图还是画类别标记 */
  kind: EvidenceKind;
}

/** 界面用的类别。`other` 只在真本认得类型但这里没细分时出现（当前不会）。 */
export type EvidenceKind = 'image' | 'audio' | 'video' | 'document' | 'other';

/** 给人看的类别名，缩略图位置画的就是它。 */
export const EVIDENCE_KIND_LABEL: Record<EvidenceKind, string> = {
  image: '图片',
  audio: '录音',
  video: '视频',
  document: '文档',
  other: '文件',
};

function kindOf(name: string, contentType: string): EvidenceKind {
  const k = attachmentKind(name, contentType);
  if (k === 'image') return 'image';
  if (k === 'audio') return 'audio';
  if (k === 'video') return 'video';
  if (k === 'document') return 'document';
  return 'other';
}

/**
 * ────────────────────────────────────────────────────────────────────────
 * 被投诉方：**两条互相独立的线**（2026-08-27 主公改口径，与管理后台一致）
 * ────────────────────────────────────────────────────────────────────────
 *
 * 改前是「人员 / 机构二选一」——那样根本表达不了「这个人和他公司我都要投」，
 * 也表达不了「我不知道是哪个业务员，就投这家公司」。现在三种都合法：
 *   · 只投人   → 只填人员那块，机构留空
 *   · 只投机构 → 只填机构那块，人员留空
 *   · 两者都投 → 两块都填
 * **留空＝本案没投这一方**（与「查不到」是两码事），至少填一块才算填了。
 * 管理后台早就是这个口径，见 `packages/web/src/drawers/CaseDrawers.tsx` 的
 * 「I2：被投诉主体拆成两条独立线」。市民端跟上，两端说的是同一件事。
 */

/**
 * 被投诉人员。
 *
 * `fromDirectory` 是这个结构里最重要的一位：
 *   true  ＝ 从协会名录里**选中**的，姓名与编号都是协会侧的原值；
 *   false ＝ 名录里查不到、市民**手工敲**的名字，只是一个说法，未经核对。
 * 受理人员据此判断该不该直接信这条信息，所以**两端都必须把它渲染出来**，
 * 也**不许**给手工填的补一个编号让它看起来像核对过。
 */
export interface RespondentPersonPick {
  fromDirectory: boolean;
  /** 姓名 */
  name: string;
  /** CD/ZL 编号。**仅名录选中时有**；手工填写恒空 */
  cdNumber: string;
  /**
   * 此人所属机构名（协会人员查询接口顺带返回）。
   *
   * ★★ **只用于界面显示，绝不进提交体**（2026-08-27 主公定）。
   *   协会人员查询接口只给机构**名字**、**不给机构编号**（实测返回键为
   *   userId/name/cdNumber/orgName/sincerityState/score/memberType/star，没有 orgId）。
   *   把这个名字写进「被投诉机构」，就造出一个**有名字没编号**的机构——
   *   后台会据此把它标成「非会员机构」，那是**错误结论**：人家可能本来就是会员，
   *   只是这条路径拿不到编号而已。同一条纪律后台也写着，见 CaseDrawers.tsx 的 `onPick`。
   *   所以它在这里只承担一件事：让市民确认「秦晓东（所属：某某公司）」选对了人。
   */
  orgName: string;
}

/** 被投诉机构。 */
export interface RespondentOrgPick {
  fromDirectory: boolean;
  /** 机构全称 */
  name: string;
  /**
   * 协会机构编号（接口的 `orgId` 转成字符串）。**仅名录选中时有**；手工填写恒空。
   *
   * ★ 机构查询接口是**给** orgId 的（实测 orgId=30561），先前顺手丢掉了——
   *   于是市民明明从协会机构库里选的，到了后台却因「有名无号」被标成非会员机构。
   *   带上它，后台才能反查、才知道这条是核对过的。
   */
  orgCode: string;
}

/** 表单当前的全部内容。两个入口的 state 形状按这个来。 */
export interface ComplaintDraft {
  name: string;
  phone: string;
  role: string;
  /**
   * 身份选「其他」时的补充说明（2026-09-03 协会令新增）。
   *
   * ★ 它**只是草稿字段**，不是新的契约字段：进提交体前由 `composeRole()` 并进 `role`，
   *   拼成 `其他：<所填>` 落进原有的 `投诉人身份`。
   * ★ `role !== '其他'` 时它必须是空串 —— 两端在换选／取消选中时当场清空，
   *   不许留残值（残值虽然被 `composeRole` 挡住，但留着就是一个随时会漏出去的雷）。
   */
  roleOther: string;
  targetPhone: string;
  /** null ＝ 本案没投人员 */
  person: RespondentPersonPick | null;
  /** null ＝ 本案没投机构 */
  org: RespondentOrgPick | null;
  text: string;
  files: readonly PickedEvidence[];
  /**
   * ★ 2026-08-27：AI 预填、市民在确认页核对过的**五项案件要素**。
   *   与上面那些不同，它不是市民在表单里敲的，而是系统按「投诉内容及诉求」算出来、
   *   再由市民逐项核对/修改的。**空是合法状态**（预填没跑成、或他不想填），
   *   校验里一个字都不拦 —— 见 `checkComplaint` 与本文件末尾「案件要素」一节。
   */
  elements: CaseElements;
}

/**
 * 被投诉人员那一行的人话描述，两端确认页共用同一句。
 *
 * ★ 括号里的所属机构**只是给人看的**（见 `RespondentPersonPick.orgName` 注释），
 *   它不会、也不许因为显示在这里就跟着进提交体。
 */
export function respondentPersonSummary(p: RespondentPersonPick | null): string {
  if (!p || !p.name.trim()) return '';
  const who = p.name.trim();
  const tail = [p.cdNumber.trim(), p.orgName.trim() ? `所属：${p.orgName.trim()}` : '']
    .filter(Boolean)
    .join(' · ');
  return tail ? `${who}（${tail}）` : who;
}

/** 被投诉机构那一行的人话描述。编号不摆给市民看——他核对的是名字对不对。 */
export function respondentOrgSummary(o: RespondentOrgPick | null): string {
  if (!o || !o.name.trim()) return '';
  return o.name.trim();
}

/** 信息来源那一行的口径，人员线与机构线共用一句 */
function sourceLabel(fromDirectory: boolean): string {
  return fromDirectory ? '协会名录选择' : '手工填写（未在名录中核对）';
}

/**
 * 人话的体积。1024 进制，与操作系统显示一致。
 *
 * ★ 2026-09-04 批 ⑭：实现搬进真本（`fmtBytes`），这里只留别名 ——
 *   后端拒绝时说的体积与前端提示里的体积必须是**同一个函数算出来的**，
 *   否则会出现「界面说 39.9MB、后端说 40.0MB」这种谁都解释不清的一字之差。
 */
export const fmtSize = fmtBytes;

/**
 * 已占用的 base64 总长度。
 *
 * ★★ **这是判断用的数，不是给人看的数**（2026-09-04 批 ⑭ 分清楚）。
 *   物理约束确实在 base64 上（body-parser 卡的是请求体），但它比原文胖 4/3；
 *   拿它当「已选多少」报给市民，会出现「我明明只选了一件 40MB 的，怎么说已选 53.4MB」。
 *   要显示请用 `usedRawBytes()`。
 */
export function usedBudget(files: readonly PickedEvidence[]): number {
  let n = 0;
  for (const f of files) n += f.contentBase64.length;
  return n;
}

/** 已选材料的**原文**合计。凡是上屏的数字都用这个。 */
export function usedRawBytes(files: readonly PickedEvidence[]): number {
  let n = 0;
  for (const f of files) n += f.bytes;
  return n;
}

/**
 * 表单校验。返回空串＝通过；非空＝**直接弹给市民看的那一句**。
 *
 * 顺序即优先级：先卡身份信息（改起来最快），再卡正文，最后卡证据（改起来最费事）。
 */
export function checkComplaint(d: ComplaintDraft): string {
  if (!d.name.trim()) return '请填写投诉人姓名';
  if (d.name.trim().length > MAX_NAME_LEN) return `投诉人姓名不超过 ${MAX_NAME_LEN} 个字`;
  if (!/^\d{11}$/.test(d.phone.trim())) return '请填写 11 位投诉人手机号';
  // ★ 被投诉方必填（2026-08-27 主公定）：先卡"是谁"，再卡"怎么联系"——
  //   顺序照着表单从上到下，市民按提示往下走一遍就能补齐，不用来回找。
  // ★ 两条线**至少填一条**即可（同日主公改口径）：只投人、只投机构、两者都投，三种都合法；
  //   两块都空才拦。所以这里判的是"一条都没有"，不是"哪一条没填"。
  const hasPerson = Boolean(d.person && d.person.name.trim());
  const hasOrg = Boolean(d.org && d.org.name.trim());
  if (!hasPerson && !hasOrg) {
    // ★ 2026-09-04 F4：这句直接弹给市民，同步改「企业」
    return '请至少填写被投诉人员或被投诉企业其中一项';
  }
  if (!/^\d{11}$/.test(d.targetPhone.trim())) {
    return '请填写 11 位被投诉方联系电话';
  }
  if (!d.text.trim()) return '请把投诉内容及诉求写清楚';
  // ★ 证据必填（2026-08-26 主公定）
  if (d.files.length === 0) return '请至少上传 1 件证据材料（合同、聊天记录、收款凭证、照片等）';
  if (d.files.length > MAX_FILES) return msgTooManyAttachments('证据材料', d.files.length);
  /* ★ 总量兜底：正常路径下 acceptEvidence 已经拦过，这里防「先选满、再删了又加」这类边角。
     判断用 base64（物理约束），报数用原文（人手上拿的是文件）—— 两者不许混，理由见 `usedBudget`。 */
  if (usedBudget(d.files) > TOTAL_B64_BUDGET) {
    return `本次证据材料太大了，一次提交合计不能超过 ${fmtSize(TOTAL_RAW_BUDGET_BYTES)}，当前已选 ${fmtSize(
      usedRawBytes(d.files),
    )}。请删掉几件体积大的，或者分两次提交。`;
  }
  return '';
}

/** 把 File 读成 dataURL */
export function readAsDataUrl(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(new Error(`读取「${f.name}」失败`));
    r.readAsDataURL(f);
  });
}

export interface AcceptResult {
  /** 收下的（调用方 append 到已有列表后面） */
  accepted: PickedEvidence[];
  /** 没收下的，**逐条写明为什么**——不是笼统一句「上传失败」 */
  rejected: string[];
}

/**
 * 收一批文件。件数、类型、单件体积、**一次提交总量**四道一起过。
 *
 * PC 拖拽与手机相册选择走的是同一个函数：拖进来的和点进来的，判断必须一致。
 *
 * ★★ 2026-09-04 批 ⑭ 改了两件事：
 *   ① 类型判断从「必须是 image/*」换成**真本白名单**（`checkAttachmentType`）——
 *      协会《优化内容9.3》要的就是「应允许双方提交录音、视频等」。
 *      判据是扩展名而不是 `File.type`：安卓选录音时 `File.type` 常常是空串或
 *      `application/octet-stream`，只认 MIME 会把真实的录音全部拒掉。
 *   ② 每一句拒绝理由都来自真本的话术函数，与后端 400 时说的**逐字相同**。
 *      改前这里自己拼「超过单张 10.0MB」，后端拼「超过 10MB」——同一件事两种说法。
 *
 * ★ 这里挡不住的只有单案累计 200MB（要读服务端索引）。它由后端 400，页面原样弹出。
 */
export async function acceptEvidence(
  existing: readonly PickedEvidence[],
  incoming: readonly File[],
  what = '证据材料',
): Promise<AcceptResult> {
  const accepted: PickedEvidence[] = [];
  const rejected: string[] = [];
  /* 两笔账并行记：`used` 是 base64（拿它判），`usedRaw` 是原文（拿它说话）。 */
  let used = usedBudget(existing);
  let usedRaw = usedRawBytes(existing);

  for (const f of incoming) {
    if (existing.length + accepted.length >= MAX_FILES) {
      rejected.push(msgTooManyAttachments(what, existing.length + accepted.length + 1));
      continue;
    }
    const badType = checkAttachmentType(what, f.name, f.type);
    if (badType) {
      rejected.push(badType);
      continue;
    }
    /* ★ 体积先判、再读文件：40MB 的上限之下，先把一件 300MB 的视频整个读成 dataURL
       再告诉人「太大了」，手机上会先卡死几秒甚至直接崩掉这一页。 */
    const tooBig = checkAttachmentBytes(what, f.name, f.size);
    if (tooBig) {
      rejected.push(tooBig);
      continue;
    }
    let dataUrl: string;
    try {
      dataUrl = await readAsDataUrl(f);
    } catch {
      rejected.push(`「${f.name}」读不出来，请换一件试试`);
      continue;
    }
    const comma = dataUrl.indexOf(',');
    const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : '';
    if (!b64) {
      rejected.push(`「${f.name}」是空文件`);
      continue;
    }
    if (used + b64.length > TOTAL_B64_BUDGET) {
      // ★ 这一句是给市民的，所以要说「怎么办」，不是只说「不行」；报的是**原文**口径
      rejected.push(msgBatchOverBudget(f.name, f.size, usedRaw));
      continue;
    }
    used += b64.length;
    usedRaw += f.size;
    const kind = kindOf(f.name, f.type);
    accepted.push({
      name: f.name,
      contentType: f.type,
      contentBase64: b64,
      // ★ 只有图片留 dataURL；别的类型留空串，界面据此画类别标记而不是碎图标
      preview: kind === 'image' ? dataUrl : '',
      bytes: f.size,
      kind,
    });
  }
  return { accepted, rejected };
}

/**
 * 确认页要复核的**一行**。
 *
 * ★ 为什么放在这里而不是各写各的（2026-08-26 加确认页时立）：
 *   手机端与 PC 端的确认页版式不一样（一个竖排卡片、一个横排定义表），
 *   但**摆出来的字段和口径必须是同一套** —— 少一项就等于让市民确认了他没看见的东西。
 *   所以「摆哪几行、每行叫什么、没填时显示什么」由本文件一处说了算，
 *   两端只负责把这些行画成各自的样子。
 */
export interface DraftRow {
  key: string;
  label: string;
  /** 已经处理成可直接显示的字符串（选填未填时是「未填写」这类人话，不是空串） */
  value: string;
  /** true＝这一行市民没填。两端据此弱化（灰字），但**照样摆出来**让他看见 */
  blank: boolean;
}

/**
 * 除「投诉内容及诉求」「证据材料」外的短字段行。
 *
 * 那两项在两端都是**整块**渲染（正文要全文、证据要缩略图），不塞进这张表。
 * 顺序＝确认页上的阅读顺序：先是谁、怎么联系，再是身份，最后是被投诉方。
 */
export function summarizeDraft(d: ComplaintDraft): DraftRow[] {
  const target = d.targetPhone.trim();
  const p = d.person && d.person.name.trim() ? d.person : null;
  const o = d.org && d.org.name.trim() ? d.org : null;
  const rows: DraftRow[] = [
    { key: 'name', label: '投诉人姓名', value: d.name.trim(), blank: false },
    { key: 'phone', label: '投诉人电话', value: d.phone.trim(), blank: false },
    /* ★ 2026-09-03：这一行摆的是 `composeRole()` 拼出来的**最终值**，与提交体逐字相同 ——
       他在「其他」后面写了字，确认页就必须让他看见那句字，而不是光看见「其他」。 */
    { key: 'role', label: '投诉人身份', value: composeRole(d.role, d.roleOther) || '未选择', blank: !d.role },
  ];
  /* ★ 人员与机构**各占一行、各带各的信息来源**（2026-08-27 两条独立线）。
     空的那一块照样摆出来，但话要说准：写「本案未投诉企业」而不是「未填写」——
     （★ 2026-09-04 F4 前这句写的是「本案未投诉机构」，随露脸文案一并改「企业」；
       这样写的**理由**没变，见下一行。）
     留空在新口径下是一个**明确的意思**（没投这一方），不是漏填。 */
  rows.push({
    key: 'respondentPerson',
    label: '被投诉人员',
    value: p ? respondentPersonSummary(p) : '本案未投诉人员',
    blank: !p,
  });
  // 手工填的必须写明"未在名录中核对"，否则受理人员会误当成协会核对过的人
  if (p) {
    rows.push({
      key: 'respondentPersonSource',
      label: '人员信息来源',
      value: sourceLabel(p.fromDirectory),
      blank: false,
    });
  }
  /* ★ 2026-09-04 F4：这两行**给人看的字**由「机构」改「企业」，两端确认页同时生效
     （手机端 SubmitConfirm、PC 端 PcConfirm 都只是把这些行画出来，不另写文案）。
     `key` 仍是 `respondentOrg` / `respondentOrgSource` —— 内部键不上屏，不动。 */
  rows.push({
    key: 'respondentOrg',
    label: '被投诉企业',
    value: o ? respondentOrgSummary(o) : '本案未投诉企业',
    blank: !o,
  });
  if (o) {
    rows.push({
      key: 'respondentOrgSource',
      label: '企业信息来源',
      value: sourceLabel(o.fromDirectory),
      blank: false,
    });
  }
  rows.push({ key: 'targetPhone', label: '被投诉方电话', value: target || '未填写', blank: !target });
  return rows;
}

/**
 * 证据那一行的计数说明，两端同一句。
 *
 * ★ 2026-09-04 批 ⑭：量词由「张」改「件」—— 放开录音与视频之后，「3 张」
 *   在一段录音加两个视频面前就是错的字。
 */
export function evidenceSummary(files: readonly PickedEvidence[]): string {
  let bytes = 0;
  for (const f of files) bytes += f.bytes;
  return `${files.length} 件 · 合计 ${fmtSize(bytes)}`;
}

/** 拼请求体。字段一律照 `CitizenSubmitBody`，一个不多一个不少。 */
export function buildSubmitBody(d: ComplaintDraft): CitizenSubmitBody {
  const 证据: CitizenAttachmentInput[] = d.files.map((f) => ({
    name: f.name,
    contentType: f.contentType,
    contentBase64: f.contentBase64,
  }));
  const body: CitizenSubmitBody = {
    投诉人姓名: d.name.trim(),
    投诉人电话: d.phone.trim(),
    被投诉人电话: d.targetPhone.trim(),
    原文: d.text.trim(),
    证据,
  };
  /* ★ 2026-09-03 协会令：身份选「其他」时把补充说明并成 `其他：<所填>` 一起送，
     **仍然只写 `投诉人身份` 这一个字段**（不新增契约字段，见 `composeRole` 注释）。 */
  const 身份 = composeRole(d.role, d.roleOther);
  if (身份) body.投诉人身份 = 身份;

  /* ── 人员线 ────────────────────────────────────────────────────────────
     ★★ 这里**故意不写** `body.被投诉机构 = p.orgName`（2026-08-27 主公定「甲」）。
       协会人员查询接口只给机构名、不给机构编号，写进去就是一个「有名字没编号」的
       机构凭据；后台见到有名无号会把它标成**非会员机构**，而那是错误结论。
       所属机构只在界面上显示（见 `RespondentPersonPick.orgName`），到此为止。 */
  const p = d.person;
  if (p && p.name.trim()) {
    body.被投诉人姓名 = p.name.trim();
    // 编号只能来自协会名录：手工填的这一项就是空，**不补**。
    // 编号有无＝「这条有没有被名录核对过」的唯一判据，受理人员据此决定要不要直接信。
    if (p.cdNumber.trim()) body.被投诉人编号 = p.cdNumber.trim();
  }

  /* ── 机构线 ────────────────────────────────────────────────────────────
     机构名**只从这条线来**，绝不来自上面那个人的所属机构。 */
  const o = d.org;
  if (o && o.name.trim()) {
    body.被投诉机构 = o.name.trim();
    // ★ 机构编号（orgId）名录里是**有**的，先前顺手丢了，导致协会库里选出来的机构
    //   在后台被当成非会员。同样只在名录选中时才有，手工填的恒空。
    if (o.orgCode.trim()) body.被投诉机构编号 = o.orgCode.trim();
  }

  /* ── 案件要素五项（AI 预填 ＋ 市民确认）─────────────────────────────────
     ★ **空的一律不进请求体**。写一个空串上去，在乐享那一行看起来与
       「AI 判不准所以留空」一模一样，但语义完全不同 —— 前者是市民确认过「就是没有」，
       后者是还没算出来。既然分不清，就一个都不写：不写＝这一项本次没给值。
     ★ 顺序照 `CITIZEN_CONFIRMED_FIELD_NAMES`（契约真本里那份写死的清单）。 */
  const e = d.elements;
  if (e.案件领域) body.案件领域 = e.案件领域;
  // `null` ＝ 没选中任何一个区。**绝不落 0**：0 在乐享是一个有业务含义的区域值。
  if (e.区域ID !== null) body.区域ID = e.区域ID;
  if (e.房产标的.trim()) body.房产标的 = e.房产标的.trim();
  if (e.投诉事项.trim()) body.投诉事项 = e.投诉事项.trim();
  if (e.诉求.trim()) body.诉求 = e.诉求.trim();
  return body;
}

/* ══════════════════════════════════════════════════════════════════════════
   案件要素（AI 预填 → 市民核对 → 随提交体落库）
   ══════════════════════════════════════════════════════════════════════════

   ★ 为什么整块放在这个文件：手机端确认页与 PC 端确认页是**两个入口、同一件事**，
     「哪五项、每项叫什么、AI 给的和用户改的怎么分、空了怎么办」必须只有一份实现。
     两端只负责把它画成各自的样子（本轮更进一步：连画法都合并成同一个组件
     `components/CaseElements.tsx`，两端只是外层容器不同）。

   ★ 三条红线，改这一节之前先读：
     ① **判不准就留空，绝不编造。** 没有默认值、没有「其他」兜底、没有占位词。
     ② **绝不挡住提交。** 本节不导出任何会让 `checkComplaint` 拦人的东西 ——
        五项全空照样能提交，那正是预填跑飞（实测约 7%）时唯一正确的行为。
     ③ **AI 给的与用户改的必须分得清**（`ElementOrigin`），但只标**数据来源**，
        不给用户贴任何身份标签。
   ────────────────────────────────────────────────────────────────────────── */

/** 案件领域的四个合法值，直接用契约真本那一份，界面按这个顺序横排。 */
export const CASE_DOMAINS = PREFILL_CASE_DOMAINS;

/**
 * 五项要素在前端的形状。
 *
 * 键名与乐享列名逐字相同（`投诉事项` 而不是界面上那个说法「投诉事由」），
 * 这样 `buildSubmitBody` 里不需要任何一层改名映射 —— 少一层映射就少一处能写错的地方。
 */
export interface CaseElements {
  /** 四选一；`''` ＝ 判不准 / 市民没选 */
  案件领域: string;
  /** 只能取自 `GET /api/citizen/areas` 那张表；`null` ＝ 没选中 */
  区域ID: number | null;
  /**
   * 区域中文名，**仅用于显示**，不进提交体。
   *
   * 它与 `区域ID` 可以不同步：模型给了一个表外的区名时 `区域ID` 是 null 而这里有值 ——
   * 那是**故意保留**的线索（界面据此提示「识别为『某某』，请在列表里确认」），
   * 但**绝不能据此造一个 ID**（契约真本 `PrefillResult.区域ID` 注释：推错了老系统删不掉）。
   */
  区域名: string;
  房产标的: string;
  /** 界面上叫「投诉事由」。**只改这一句概括，改不到原文** */
  投诉事项: string;
  诉求: string;
}

/** 什么都还没有的那一份。**降级态就长这样**，并且它是完全可以提交的。 */
export const EMPTY_ELEMENTS: CaseElements = {
  案件领域: '',
  区域ID: null,
  区域名: '',
  房产标的: '',
  投诉事项: '',
  诉求: '',
};

/**
 * 界面上的五行。`region` 一行同时管 `区域ID` 与 `区域名`，所以它自己一个键。
 */
export type ElementKey = 'domain' | 'region' | 'property' | 'matter' | 'demand';

/** 阅读顺序＝界面从上到下的顺序 */
export const ELEMENT_KEYS: readonly ElementKey[] = ['domain', 'region', 'property', 'matter', 'demand'];

/**
 * 每一行给市民看的名字。
 *
 * ⚠ `matter` 叫「投诉事由」是**界面措辞**，字段名仍是 `投诉事项`（乐享列名）。
 *   两者不一致是主公定的，不许为了「统一」把任何一边改掉。
 */
export const ELEMENT_LABELS: Record<ElementKey, string> = {
  domain: '案件领域',
  region: '区域',
  property: '房产标的',
  matter: '投诉事由',
  demand: '诉求',
};

/**
 * 这一项现在的值是**哪来的**。
 *
 *   blank  还没有值（预填没给、市民也没填）
 *   ai     系统按投诉内容及诉求算出来的，市民还没动过
 *   user   市民自己填的或改过的
 *
 * ★ 只标**数据来源**，不是给用户贴标签。界面上因此只出现「AI 判定」「AI 摘要」
 *   「AI 提取」「已修改」这四个词，没有任何描述用户本人的字眼。
 */
export type ElementOrigin = 'blank' | 'ai' | 'user';

export type ElementOrigins = Record<ElementKey, ElementOrigin>;

/** 一份全空的来源表。 */
export const BLANK_ORIGINS: ElementOrigins = {
  domain: 'blank',
  region: 'blank',
  property: 'blank',
  matter: 'blank',
  demand: 'blank',
};

/**
 * 某一行的来源徽标文案。`null` ＝ 不摆徽标（这一项还是空的，摆个标签只是噪声）。
 *
 * 措辞按行分：领域是**判定**（从四个里挑一个），事由是**摘要**（把一段话概括成一句），
 * 其余是**提取**（原文里本来就有，只是拣出来）。三个词说的是三件不同的事，不合并。
 */
export function originBadge(key: ElementKey, origin: ElementOrigin): string | null {
  if (origin === 'blank') return null;
  if (origin === 'user') return '已修改';
  if (key === 'domain') return 'AI 判定';
  if (key === 'matter') return 'AI 摘要';
  return 'AI 提取';
}

/** 每一项的字数上限，取自契约真本。界面用它设 `maxLength`，超不了就不会被后端 400。 */
export const ELEMENT_MAXLEN: Record<Exclude<ElementKey, 'domain' | 'region'>, number> = {
  property: PREFILL_LIMITS.房产标的Max,
  matter: PREFILL_LIMITS.投诉事项Max,
  demand: PREFILL_LIMITS.诉求Max,
};

/**
 * 把一次预填结果并进当前要素。
 *
 * ★★ **只填市民没动过的项**（`origin !== 'user'`）。
 *   理由与提交体那条口径同源：**市民填了就以市民为准，AI 只补空缺** ——
 *   他改完事由又回去补了两句投诉内容及诉求，重算一次就把他改的覆盖掉，
 *   那是这一整个功能最容易做砸、也最招人恨的一种做法。
 *
 * ★ 空值不覆盖已有值：预填这一次没判出来的项，保持原样（可能是上一次判出来的），
 *   不要用空串把它擦掉 —— 擦掉之后界面上看起来就像「这一项从来没有过」。
 *
 * ★ 超长的 AI 值**截到上限**而不是丢掉：契约写着 ≤20 字，但实测约 7% 会跑飞，
 *   真送来一句 40 字的概括，原样带进提交体会被后端 400 顶回来 —— 那就等于
 *   AI 跑飞连累市民提交不了。截断后市民在确认页上看得见、改得动，这是可控的。
 */
export function applyPrefill(
  cur: CaseElements,
  origins: ElementOrigins,
  r: PrefillResult,
): { elements: CaseElements; origins: ElementOrigins } {
  const next: CaseElements = { ...cur };
  const nextOrigins: ElementOrigins = { ...origins };

  const takeText = (
    key: Exclude<ElementKey, 'domain' | 'region'>,
    field: '房产标的' | '投诉事项' | '诉求',
  ): void => {
    if (origins[key] === 'user') return;
    const v = r[field].trim().slice(0, ELEMENT_MAXLEN[key]);
    if (!v) return;
    next[field] = v;
    nextOrigins[key] = 'ai';
  };

  if (origins.domain !== 'user') {
    // 四个合法值之外的一律丢掉（契约真本已经在服务端过滤过一道，这里是第二道）
    const d = (CASE_DOMAINS as readonly string[]).includes(r.案件领域) ? r.案件领域 : '';
    if (d) {
      next.案件领域 = d;
      nextOrigins.domain = 'ai';
    }
  }

  if (origins.region !== 'user') {
    // ID 与名字**同进同出**：只有 ID 落在表里（服务端已保证）才算这一项有值。
    // 名字有、ID 没有的那种情形照样把名字留下 —— 界面拿它提示市民自己去下拉里确认，
    // 但 `区域ID` 保持 null，绝不自己造一个。
    if (r.区域ID !== null) {
      next.区域ID = r.区域ID;
      next.区域名 = r.区域.trim();
      nextOrigins.region = 'ai';
    } else if (r.区域.trim() && next.区域ID === null) {
      next.区域名 = r.区域.trim();
      // 注意：这里**不**把 origin 标成 'ai' —— 这一项其实还是空的（没有 ID 就落不了库），
      // 标成 ai 会让界面挂一个「AI 提取」的徽标，而实际上什么都没提取到，那是假状态。
    }
  }

  takeText('property', '房产标的');
  takeText('matter', '投诉事项');
  takeText('demand', '诉求');

  return { elements: next, origins: nextOrigins };
}

/**
 * 市民改了某一行。返回新的要素与来源表。
 *
 * ★ **改成空也算「改过」**：他把 AI 填的事由整句删掉，是一个明确的意思
 *   （「这句概括不对，我不要它」），不是回到「AI 还没算」。所以 origin 记 `user`，
 *   下一次重算也不会把它填回去 —— 否则他每删一次，系统就填回来一次。
 */
export function editElement(
  cur: CaseElements,
  origins: ElementOrigins,
  key: ElementKey,
  patch: Partial<CaseElements>,
): { elements: CaseElements; origins: ElementOrigins } {
  return {
    elements: { ...cur, ...patch },
    origins: { ...origins, [key]: 'user' },
  };
}

/** 这一行现在有没有值。界面据此决定摆不摆徽标、要不要压成灰字。 */
export function elementFilled(e: CaseElements, key: ElementKey): boolean {
  switch (key) {
    case 'domain':
      return Boolean(e.案件领域);
    case 'region':
      return e.区域ID !== null;
    case 'property':
      return Boolean(e.房产标的.trim());
    case 'matter':
      return Boolean(e.投诉事项.trim());
    case 'demand':
      return Boolean(e.诉求.trim());
  }
}

/** 五项一个都没有。降级态的判据，两端同一句。 */
export function elementsAllBlank(e: CaseElements): boolean {
  return ELEMENT_KEYS.every((k) => !elementFilled(e, k));
}

/**
 * ★★ 「留空也能提交」这句承诺**全站只有这一处** ★★
 *
 * 它现在是**真话**：五项在契约真本 `CitizenConfirmedFields` 里全是可选，
 * `checkComplaint` 一条都不拦。而且这句话是**必要的** —— 预填跑飞（实测约 7%）时，
 * 市民面对五个空栏，不说这一句他就会以为自己被卡住了。
 *
 * ⚠ 但它随时可能变成半句假话（2026-08-27 后端提出，主公**尚未拍板**）：
 *   后端已把 `区域ID` 的 AI 自动兜底移出结构化范围 —— 市民在确认页把区域留空，
 *   之后**再没有任何人会补它**，推老系统就缺 `AreaID`；后端因此建议区域改必选。
 *   真要改的那一天，动的是：本常量 ＋ 引用它的两处（`elementsHint` 与区域那一行的
 *   `el-note-tail`）＋ `checkComplaint` 加一条。别处一个字都不用动 ——
 *   这句话之所以抽成常量，就是为了那一天。
 *   **在主公拍板之前保持现状（可空）**，措辞也不许提前改成「必填」那种假状态。
 */
export const ELEMENTS_OPTIONAL_NOTE = '留空也能提交';

/**
 * 「案件要素」那一块的**状态说明**，两端同一句。返回空串＝什么都不用说。
 *
 * ★ 本项目一条不许破的纪律（与 `StructuringStatus` 同源）：
 *   **「没跑成功」绝不能看起来像「本来就没内容」。**
 *   所以 `timeout`/`failed` 与 `ok` 但全空，说的是两句不同的话。
 * ★ 每一句结尾都接 `ELEMENTS_OPTIONAL_NOTE` —— 这一屏最怕的就是市民以为自己被卡住了。
 */
export function elementsHint(status: PrefillStatus | null, e: CaseElements, running: boolean): string {
  if (running) return '';
  if (!elementsAllBlank(e)) return '';
  // 常量本身就是一句完整的话（「留空也能提交」），前面不要再接「也可以」——
  // 接了会拼出「也可以留空也能提交」。2026-08-27 实测截图里抓到过一次。
  const tail = `您可以自己填，${ELEMENTS_OPTIONAL_NOTE}。`;
  if (status === 'timeout' || status === 'failed') {
    return `这几项这次没能自动整理出来，${tail}`;
  }
  if (status === 'ok') {
    return `从您写的内容里没能判断出这几项，${tail}`;
  }
  return `这几项${tail}`;
}
